import { Type } from '@sinclair/typebox';
// HUB-107 / COP-152 — MCP server registry + per-user connection contracts.
// Secret material (OAuth client secret, shared key, user keys, tokens) is
// ABSENT from every response schema; only presence booleans are exposed.
/** The closed menu of connect types (HUB-107 §3). */
export const MCP_AUTH_TYPES = ['oauth', 'user_key', 'hub_identity', 'shared_key'];
export const McpAuthTypeSchema = Type.Union([
    Type.Literal('oauth'),
    Type.Literal('user_key'),
    Type.Literal('hub_identity'),
    Type.Literal('shared_key'),
]);
/** Server slug — becomes the `mcp_<slug>_` tool-name prefix. */
export const MCP_SERVER_SLUG_PATTERN = '^[a-z][a-z0-9]{1,23}$';
/** One admin-approved tool; description + schema frozen at approval (COP-152 §2). */
export const McpApprovedToolSchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 128 }),
    description: Type.String({ maxLength: 4000 }),
    inputSchema: Type.Record(Type.String(), Type.Unknown()),
    kind: Type.Union([Type.Literal('read'), Type.Literal('write')]),
    confirmationMessage: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
});
export const McpServerSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    slug: Type.String(),
    displayName: Type.String(),
    url: Type.String(),
    authType: McpAuthTypeSchema,
    oauthClientId: Type.Union([Type.String(), Type.Null()]),
    hasOauthClientSecret: Type.Boolean(),
    oauthScopes: Type.Union([Type.String(), Type.Null()]),
    keyHeader: Type.Union([Type.String(), Type.Null()]),
    hasSharedKey: Type.Boolean(),
    tools: Type.Array(McpApprovedToolSchema),
    enabled: Type.Boolean(),
    /** The OAuth redirect URI to register with the IdP (oauth servers). */
    callbackUrl: Type.String(),
    connectionCount: Type.Integer(),
    updatedAt: Type.String({ format: 'date-time' }),
});
export const McpServerListResponseSchema = Type.Object({ servers: Type.Array(McpServerSchema) });
/**
 * Create/update body. Write-only secrets: omitted = keep, `null` = clear,
 * a string = set.
 */
export const McpServerWriteBodySchema = Type.Object({
    slug: Type.String({ pattern: MCP_SERVER_SLUG_PATTERN }),
    displayName: Type.String({ minLength: 1, maxLength: 120 }),
    url: Type.String({ minLength: 1, maxLength: 2000 }),
    authType: McpAuthTypeSchema,
    oauthClientId: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    oauthClientSecret: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    oauthScopes: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    keyHeader: Type.Optional(Type.Union([Type.String({ pattern: '^[A-Za-z0-9-]{1,64}$' }), Type.Null()])),
    sharedKey: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    tools: Type.Optional(Type.Array(McpApprovedToolSchema)),
    enabled: Type.Optional(Type.Boolean()),
});
export const McpServerIdParamsSchema = Type.Object({ id: Type.String({ format: 'uuid' }) });
/** One live tool from discover, compared with the approved allowlist. */
export const McpDiscoveredToolSchema = Type.Object({
    name: Type.String(),
    description: Type.String(),
    inputSchema: Type.Record(Type.String(), Type.Unknown()),
    readOnlyHint: Type.Optional(Type.Boolean()),
    /** The model-facing name, or null when it breaks the provider name rule. */
    toolName: Type.Union([Type.String(), Type.Null()]),
    approved: Type.Boolean(),
    /** Approved, but the live description or schema differs from the frozen one. */
    changed: Type.Boolean(),
});
export const McpDiscoverResponseSchema = Type.Object({ tools: Type.Array(McpDiscoveredToolSchema) });
/** The user's view of one server (`GET /mcp-connections`). */
export const McpUserServerSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    slug: Type.String(),
    displayName: Type.String(),
    authType: McpAuthTypeSchema,
    status: Type.Union([
        Type.Literal('connected'),
        Type.Literal('not_connected'),
        Type.Literal('credential_invalid'),
        Type.Literal('automatic'),
    ]),
});
export const McpUserServerListResponseSchema = Type.Object({ servers: Type.Array(McpUserServerSchema) });
export const McpConnectionParamsSchema = Type.Object({ serverId: Type.String({ format: 'uuid' }) });
export const McpUserKeyBodySchema = Type.Object({ key: Type.String({ minLength: 1, maxLength: 4000 }) });
//# sourceMappingURL=mcp-servers.js.map